import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ChangeDetectionStrategy, Component, forwardRef, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';

import { CustomFormControlComponent } from '../../../core/abstractions/custom-form-control.component';
import { OptionalType } from '../../../models/types/optional.type';

@Component({
    selector: 'sl-cron-input',
    templateUrl: './cron-input.component.html',
    styleUrls: ['./cron-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: CronInputComponent
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => CronInputComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CronInputComponent extends CustomFormControlComponent<OptionalType<string>> implements OnInit {
    @Input() labelKey: string;

    readonly separatorKeysCodes = [ENTER, COMMA];

    readonly daysOfMonth = [
        1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31
    ];
    readonly daysOfWeek = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
    readonly months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

    parts: FormGroup;

    ngOnInit(): void {
        this.parts = new FormGroup({
            hour: new FormControl(undefined, Validators.required),
            minute: new FormControl(undefined, Validators.required),
            daysOfMonth: new FormControl(),
            months: new FormControl(),
            daysOfWeek: new FormControl()
        });

        this.parts.valueChanges.subscribe(() => {
            this.value = this.buildCron();
        });
    }

    override writeValue(value: string): void {
        if (!this.isValidCron(value)) {
            // Cron is invalid, set to undefined
            this.value = undefined;
            this.parts.patchValue({ hour: undefined, minute: undefined, daysOfMonth: undefined, months: undefined, daysOfWeek: undefined });
            return;
        }

        super.writeValue(value);

        // fill inputs
        const values = value.split(' ');
        const minute = parseInt(values[1], 10);
        const hour = parseInt(values[2], 10);
        const daysOfMonthStr = values[3];
        const daysOfMonth = daysOfMonthStr === '?' || daysOfMonthStr === '*' ? [] : daysOfMonthStr.split(',').map(d => parseInt(d, 10));
        const monthsStr = values[4];
        const months = monthsStr === '*' ? [] : monthsStr.split(',');
        const daysOfWeekStr = values[5];
        const daysOfWeek = daysOfWeekStr === '?' ? [] : daysOfWeekStr.split(',');

        this.parts.patchValue({ hour, minute, daysOfMonth, months, daysOfWeek });
    }

    override setDisabledState(isDisabled: boolean): void {
        super.setDisabledState(isDisabled);
        if (isDisabled) {
            this.parts.disable();
        } else {
            this.parts.enable();
        }
    }

    override validate(): ValidationErrors | null {
        // eslint-disable-next-line no-null/no-null
        return this.parts.valid ? null : { invalid: true };
    }

    private isValidCron(cron: string): boolean {
        if (!cron) {
            return false;
        }

        const values = cron.split(' ');

        if (values.length !== 6) {
            return false;
        }

        const second = parseInt(values[0], 10);
        const minute = parseInt(values[1], 10);
        const hour = parseInt(values[2], 10);
        const daysOfMonthStr = values[3];
        const daysOfMonth = daysOfMonthStr.split(',');
        const monthsStr = values[4];
        const months = monthsStr.split(',');
        const daysOfWeekStr = values[5];
        const daysOfWeek = daysOfWeekStr.split(',');

        if (second !== 0) {
            return false;
        }
        if (isNaN(minute) || isNaN(hour)) {
            return false;
        }
        if (minute < 0 || minute > 59 || hour < 0 || hour > 23) {
            return false;
        }
        if (
            daysOfMonthStr !== '?'
            && daysOfMonthStr !== '*'
            && !daysOfMonth.map(d => parseInt(d, 10)).every(d => this.daysOfMonth.includes(d))
        ) {
            return false;
        }
        if (monthsStr !== '*' && !months.every(m => this.months.includes(m))) {
            return false;
        }
        if (daysOfWeekStr !== '?' && !daysOfWeek.every(d => this.daysOfWeek.includes(d))) {
            return false;
        }

        return true;
    }

    private buildCron(): OptionalType<string> {
        const value = this.parts.value;

        const months = value.months?.length > 0 ? value.months.join(',') : '*';
        const daysOfWeek = value.daysOfWeek?.length > 0 ? value.daysOfWeek.join(',') : '?';
        const daysOfMonth = value.daysOfMonth?.length > 0 ? value.daysOfMonth.join(',') : daysOfWeek === '?' ? '*' : '?';

        if (value.minute === undefined || value.hour === undefined) {
            return undefined;
        }
        const cron = `0 ${value.minute} ${value.hour} ${daysOfMonth} ${months} ${daysOfWeek}`;
        return this.isValidCron(cron) ? cron : undefined;
    }
}
